import Image from "next/image"
import Link from "next/link"
import { ArrowRight, Sparkles } from "lucide-react"

import { FloatingElement, MotionDiv, blurIn, fadeInLeft, fadeInRight, imageReveal, scaleIn } from "@/components/motion"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import {
  EditorialHeroAmbientGlow,
  EditorialHeroImageWrap,
  EditorialHeroMagneticCta,
  EditorialHeroMetricLift,
} from "./editorial-hero-motion"

export type EditorialHeroMetric = {
  value: string
  label: string
}

export type EditorialHeroProps = {
  eyebrow?: string
  title: string
  highlight?: string
  description: string
  primaryCta?: { label: string; href: string }
  secondaryCta?: { label: string; href: string }
  imageSrc?: string
  imageAlt?: string
  metrics?: EditorialHeroMetric[]
  className?: string
}

/** Art-directed hero with editorial typography, optional media, and proof metrics. */
export function EditorialHero({
  eyebrow,
  title,
  highlight,
  description,
  primaryCta,
  secondaryCta,
  imageSrc,
  imageAlt = "",
  metrics = [],
  className,
}: EditorialHeroProps) {
  return (
    <section className={cn("relative isolate overflow-hidden bg-background", className)}>
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_18%_18%,hsl(var(--primary)/0.16),transparent_30%),linear-gradient(180deg,hsl(var(--background)),hsl(var(--muted)/0.48))]" />
      <FloatingElement intensity={14} duration={8} className="absolute left-1/2 top-10 -z-10 h-64 w-[42rem] -translate-x-1/2 rounded-full bg-primary/10 blur-3xl" />
      <EditorialHeroAmbientGlow className="right-[-8rem] top-1/3 h-[28rem] w-[28rem]" />

      <div className="mx-auto grid min-h-[calc(100vh-4rem)] w-full max-w-7xl items-center gap-12 px-4 py-20 sm:px-6 lg:grid-cols-[1.02fr_0.98fr] lg:px-8 lg:py-24">
        <MotionDiv variants={fadeInLeft} className="max-w-3xl">
          {eyebrow && (
            <Badge variant="secondary" className="mb-6 gap-2 rounded-full border border-primary/10 bg-primary/8 px-3 py-1 text-primary">
              <Sparkles className="size-3.5" />
              {eyebrow}
            </Badge>
          )}

          <h1 className="font-heading text-5xl font-semibold leading-[0.94] tracking-tight text-foreground sm:text-6xl lg:text-7xl">
            {title}
            {highlight && <span className="block text-primary">{highlight}</span>}
          </h1>

          <p className="mt-7 max-w-2xl text-lg leading-8 text-muted-foreground sm:text-xl">
            {description}
          </p>

          {(primaryCta || secondaryCta) && (
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              {primaryCta && (
                <Button asChild size="xl" className="group rounded-button">
                  <Link href={primaryCta.href}>
                    {primaryCta.label}
                    <ArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </Button>
              )}
              {secondaryCta && (
                <Button asChild variant="outline" size="xl" className="rounded-button border-border/70 bg-background/70">
                  <Link href={secondaryCta.href}>{secondaryCta.label}</Link>
                </Button>
              )}
            </div>
          )}

          {metrics.length > 0 && (
            <dl className="mt-12 grid max-w-2xl grid-cols-2 gap-4 sm:grid-cols-3">
              {metrics.map((metric) => (
                <MotionDiv key={metric.label} variants={scaleIn}>
                  <EditorialHeroMetricLift className="rounded-card border border-border/70 bg-card/72 p-4 shadow-sm backdrop-blur">
                    <dt className="text-sm leading-5 text-muted-foreground">{metric.label}</dt>
                    <dd className="mt-2 font-heading text-2xl font-semibold tracking-tight text-foreground">{metric.value}</dd>
                  </EditorialHeroMetricLift>
                </MotionDiv>
              ))}
            </dl>
          )}
        </MotionDiv>

        <MotionDiv variants={fadeInRight} className="relative min-h-[28rem] lg:min-h-[40rem]">
          <div className="absolute inset-x-8 bottom-0 top-16 rounded-panel bg-primary/10 blur-3xl" />
          <EditorialHeroImageWrap className="relative min-h-[28rem] overflow-hidden rounded-panel border border-border/70 bg-card shadow-[0_24px_80px_hsl(var(--foreground)/0.14)] lg:min-h-[40rem]">
            {imageSrc ? (
              <MotionDiv variants={imageReveal} className="absolute inset-0">
                <Image src={imageSrc} alt={imageAlt} fill sizes="(min-width: 1024px) 48vw, 100vw" className="object-cover" priority />
              </MotionDiv>
            ) : (
              <MotionDiv variants={blurIn} className="flex h-full min-h-[28rem] flex-col justify-between bg-[linear-gradient(135deg,hsl(var(--primary)/0.18),hsl(var(--accent))),radial-gradient(circle_at_78%_20%,hsl(var(--primary)/0.26),transparent_34%)] p-8">
                <div className="h-28 w-28 rounded-full border border-primary/20 bg-background/70 shadow-[var(--shadow-glow)]" />
                <div className="space-y-4">
                  <div className="h-3 w-3/4 rounded-full bg-primary/30" />
                  <div className="h-3 w-1/2 rounded-full bg-foreground/18" />
                  <div className="grid grid-cols-3 gap-3 pt-4">
                    <div className="h-24 rounded-card bg-background/70" />
                    <div className="h-24 rounded-card bg-primary/20" />
                    <div className="h-24 rounded-card bg-background/70" />
                  </div>
                </div>
              </MotionDiv>
            )}
          </EditorialHeroImageWrap>
        </MotionDiv>
      </div>
    </section>
  )
}
